import type { ModelGrid } from './grid';

/**
 * The wind API (worker/, same origin as the app):
 *
 *   GET /api/wind/index.json
 *     The latest complete run of each model: WindIndex. Short-lived (a minute); poll it for new runs.
 *   GET /api/wind/{model}/{run}/{valid}/{level}/{bx}/{by}.bin
 *     One block of 10 m wind (see levels.ts), gzipped (see encodeBlock). Never changes: cached for good.
 *
 * {run} and {valid} are UTC stamps `YYYYMMDDHHmm` (see `stamp`).
 */
export const WIND_API = '/api/wind';

/** Wind is stored in steps of 0.1 m/s, Open-Meteo's own precision. */
const SCALE = 10;
/** Stored where the model has no value. */
const NO_DATA = -32768;
/** "WND1": format and version. */
const MAGIC = 0x574e4431;
const HEADER = 8;

export interface WindModelInfo {
  id: string;
  /** Grid spacing, km, roughly: finer wins where models overlap. */
  km: number;
  /** Run shown, ISO time. */
  run: string;
  /** Valid times of the run, ISO, ascending. */
  times: string[];
  /** The native grid (level 0); coarser levels follow from levelGrid. */
  grid: ModelGrid;
  levels: number;
}

export interface WindIndex {
  /** Points a block spans per side, less one (see levels.ts). */
  block: number;
  models: WindModelInfo[];
}

const pad = (n: number, width = 2) => String(n).padStart(width, '0');

/** A time (ms since the epoch) as `YYYYMMDDHHmm`, UTC. */
export function stamp(time: number): string {
  const d = new Date(time);
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}`;
}

/** The time of a `YYYYMMDDHHmm` stamp, ms since the epoch; NaN if malformed. */
export function parseStamp(s: string): number {
  const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(s);
  if (!m) return NaN;
  const [y, mo, d, h, mi] = m.slice(1).map(Number);
  const time = Date.UTC(y, mo - 1, d, h, mi);
  return stamp(time) === s ? time : NaN;
}

export interface BlockKey {
  model: string;
  /** ms since the epoch. */
  run: number;
  valid: number;
  level: number;
  bx: number;
  by: number;
}

export const blockPath = ({ model, run, valid, level, bx, by }: BlockKey) =>
  `${WIND_API}/${model}/${stamp(run)}/${stamp(valid)}/${level}/${bx}/${by}.bin`;

/** The block a path names, or null if it isn't a block path. */
export function parseBlockPath(path: string): BlockKey | null {
  const m = /^\/api\/wind\/([a-z0-9_]+)\/(\d{12})\/(\d{12})\/(\d)\/(\d{1,4})\/(\d{1,4})\.bin$/.exec(path);
  if (!m) return null;
  const run = parseStamp(m[2]);
  const valid = parseStamp(m[3]);
  if (Number.isNaN(run) || Number.isNaN(valid)) return null;
  return { model: m[1], run, valid, level: Number(m[4]), bx: Number(m[5]), by: Number(m[6]) };
}

/** Wind of one block: `width`·`height` points row by row from the south, m/s, NaN where unknown. */
export interface WindBlock {
  width: number;
  height: number;
  u: Float32Array;
  v: Float32Array;
}

/**
 * A block as bytes: an 8-byte header ("WND1", width, height as uint16), then U and V as int16 tenths of m/s,
 * little-endian. Each row stores differences to the point before it (wrapping around 16 bits), which
 * gzip packs far better than the values: wind changes little from one point to the next.
 */
export function encodeBlock({ width, height, u, v }: WindBlock): Uint8Array {
  const n = width * height;
  const bytes = new Uint8Array(HEADER + 4 * n);
  const view = new DataView(bytes.buffer);
  view.setUint32(0, MAGIC);
  view.setUint16(4, width, true);
  view.setUint16(6, height, true);
  let offset = HEADER;
  for (const values of [u, v]) {
    for (let j = 0; j < height; j++) {
      let previous = 0;
      for (let i = 0; i < width; i++) {
        const x = values[j * width + i];
        const q = Number.isNaN(x) ? NO_DATA : Math.max(-32767, Math.min(32767, Math.round(x * SCALE)));
        // setInt16 keeps the low 16 bits: the difference wraps, and decoding wraps it back.
        view.setInt16(offset, q - previous, true);
        previous = q;
        offset += 2;
      }
    }
  }
  return bytes;
}

export function decodeBlock(bytes: Uint8Array): WindBlock {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.byteLength < HEADER || view.getUint32(0) !== MAGIC) throw new Error('Not a wind block');
  const width = view.getUint16(4, true);
  const height = view.getUint16(6, true);
  const n = width * height;
  if (bytes.byteLength !== HEADER + 4 * n) throw new Error('Truncated wind block');
  const out = { u: new Float32Array(n), v: new Float32Array(n) };
  // Sums wrap around 16 bits, undoing the encoder's wrapped differences.
  const q = new Int16Array(1);
  let offset = HEADER;
  for (const values of [out.u, out.v]) {
    for (let j = 0; j < height; j++) {
      q[0] = 0;
      for (let i = 0; i < width; i++) {
        q[0] += view.getInt16(offset, true);
        offset += 2;
        values[j * width + i] = q[0] === NO_DATA ? NaN : q[0] / SCALE;
      }
    }
  }
  return { width, height, ...out };
}

async function pipe(bytes: Uint8Array, transform: CompressionStream | DecompressionStream) {
  const stream = new Blob([bytes as Uint8Array<ArrayBuffer>]).stream().pipeThrough(transform);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export const gzip = (bytes: Uint8Array) => pipe(bytes, new CompressionStream('gzip'));
export const gunzip = (bytes: Uint8Array) => pipe(bytes, new DecompressionStream('gzip'));

/** Fetches and decodes one block of the wind API. */
export async function fetchWindBlock(key: BlockKey, signal?: AbortSignal): Promise<WindBlock> {
  const res = await fetch(blockPath(key), { signal });
  if (!res.ok) throw new Error(`Wind block ${blockPath(key)}: ${res.status}`);
  return decodeBlock(await gunzip(new Uint8Array(await res.arrayBuffer())));
}
